import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Heart, Lock, Clock, Sparkles, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GiftView = ({ giftId }) => {
  const [meta, setMeta] = useState(null); // null=loading, object, or 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'awaiting' | 'paid' | 'cancelled'
  const popupRef = useRef(null);
  const pollRef = useRef(null);
  const sessionIdRef = useRef(null);

  // Load metadata
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/gift/${giftId}`)
      .then((res) => { if (alive) setMeta(res.data); })
      .catch((e) => {
        if (!alive) return;
        const code = e.response?.status;
        setMeta('error');
        setErrorMsg(code === 404 ? 'This gift link does not exist.' : 'Could not load gift.');
      });
    return () => { alive = false; };
  }, [giftId]);

  // Cleanup
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (popupRef.current && !popupRef.current.closed) {
      try { popupRef.current.close(); } catch (_) { /* ignore */ }
    }
  }, []);

  const triggerDownload = useCallback(async (sessionId) => {
    try {
      const url = `${API}/gift/${giftId}/download?session_id=${encodeURIComponent(sessionId)}`;
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('HD file downloading…');
    } catch (e) {
      console.error(e);
      toast.error('Download failed.');
    }
  }, [giftId]);

  // Listen for postMessage from Stripe success popup
  useEffect(() => {
    const onMsg = async (e) => {
      if (e.origin !== window.location.origin) return;
      if (!e.data || e.data.type !== 'stickr-payment-complete') return;
      if (sessionIdRef.current && e.data.sessionId === sessionIdRef.current) {
        if (pollRef.current) clearInterval(pollRef.current);
        try { if (popupRef.current && !popupRef.current.closed) popupRef.current.close(); } catch (_) { /* ignore */ }
        setPaymentStatus('paid');
        await triggerDownload(e.data.sessionId);
        setBusy(false);
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [triggerDownload]);

  const handleBuy = useCallback(async () => {
    if (busy || !meta || meta === 'error' || meta.expired) return;
    const popup = window.open('about:blank', 'stickr_gift_checkout', 'width=520,height=720');
    if (!popup) {
      toast.error('Allow popups to complete payment.');
      return;
    }
    popupRef.current = popup;
    setBusy(true);
    setPaymentStatus(null);

    try {
      const res = await axios.post(`${API}/checkout/create-session`, {
        package_id: 'export_hd',
        origin_url: window.location.origin,
        metadata: { gift_id: giftId, kind: 'gift_purchase' },
      });
      const { url, session_id } = res.data || {};
      if (!url) throw new Error('No checkout url');
      sessionIdRef.current = session_id;
      try { popup.location.href = url; } catch (_) { /* ignore */ }
      setPaymentStatus('awaiting');

      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts += 1;
        try {
          const s = await axios.get(`${API}/checkout/status/${session_id}`);
          if (s.data?.payment_status === 'paid') {
            clearInterval(pollRef.current);
            try { if (popupRef.current && !popupRef.current.closed) popupRef.current.close(); } catch (_) { /* ignore */ }
            setPaymentStatus('paid');
            await triggerDownload(session_id);
            setBusy(false);
            return;
          }
        } catch (_) { /* keep polling */ }
        if (popupRef.current && popupRef.current.closed && attempts > 2) {
          clearInterval(pollRef.current);
          setPaymentStatus('cancelled');
          setBusy(false);
        }
        if (attempts > 150) {
          clearInterval(pollRef.current);
          setPaymentStatus('cancelled');
          setBusy(false);
          toast.error('Payment timed out.');
        }
      }, 2000);
    } catch (e) {
      console.error(e);
      toast.error('Could not start checkout');
      try { popup.close(); } catch (_) { /* ignore */ }
      setBusy(false);
    }
  }, [busy, meta, giftId, triggerDownload]);

  // ---------- render ----------
  if (meta === null) {
    return <CenteredCard><Loader2 className="h-6 w-6 animate-spin" /></CenteredCard>;
  }
  if (meta === 'error') {
    return (
      <CenteredCard>
        <AlertTriangle className="h-8 w-8 mb-3" />
        <h1 data-testid="gift-error" className="font-outfit font-black text-2xl mb-1">Hmm…</h1>
        <p className="font-work text-sm text-ink/70">{errorMsg}</p>
        <a href="/" className="neo-btn bg-paper h-10 px-4 text-sm mt-5">← Back to StckrStckr</a>
      </CenteredCard>
    );
  }
  if (meta.expired) {
    return (
      <CenteredCard>
        <Clock className="h-8 w-8 mb-3" />
        <h1 data-testid="gift-expired" className="font-outfit font-black text-2xl mb-1">This gift expired</h1>
        <p className="font-work text-sm text-ink/70 max-w-xs">Gifts live for 30 days. Ask the creator to share a fresh one.</p>
        <a href="/" className="neo-btn bg-paper h-10 px-4 text-sm mt-5">Make your own →</a>
      </CenteredCard>
    );
  }

  const previewSrc = `${API}/gift/${giftId}/preview`;
  return (
    <div data-testid="gift-view" className="min-h-screen bg-canvas grain p-4 sm:p-8 flex flex-col items-center">
      {/* nav */}
      <div className="w-full max-w-3xl flex items-center justify-between mb-8">
        <a href="/" className="flex items-center gap-2">
          <div className="h-8 w-8 border-2 border-ink bg-lilac shadow-brutSm rotate-[-6deg]" />
          <div className="h-8 w-8 border-2 border-ink bg-peach shadow-brutSm -ml-3 rotate-[6deg]" />
          <span className="ml-1 font-outfit font-black text-lg tracking-tight">StckrStckr</span>
        </a>
        <span className="label-tiny bg-paper border-2 border-ink px-2 py-1 shadow-brutSm">A gift collage</span>
      </div>

      <div className="w-full max-w-3xl grid md:grid-cols-[1fr_320px] gap-6 lg:gap-10 items-start">
        {/* preview */}
        <div className="neo-card p-4 sm:p-6">
          <span className="label-tiny block mb-3">Preview · {meta.format.toUpperCase()}</span>
          <div className="bg-canvasbg border-2 border-ink rounded-sm overflow-hidden flex items-center justify-center" style={{ aspectRatio: '1/1' }}>
            <img
              data-testid="gift-preview-image"
              src={previewSrc}
              alt="Gift collage preview"
              className="max-w-full max-h-full object-contain select-none"
              draggable={false}
            />
          </div>
          <p className="font-work text-xs text-ink/60 mt-3 leading-tight">
            Small clean preview · Buy to download full {meta.format === 'png' ? 'PNG with transparency' : 'JPG'}.
          </p>
        </div>

        {/* CTA card */}
        <div className="neo-card p-6 md:sticky md:top-8">
          <div className="flex items-center gap-2 mb-2">
            <Heart className="h-4 w-4 fill-rose stroke-ink" />
            <span className="font-outfit font-bold text-sm">Someone made you this</span>
          </div>
          <p className="font-work text-sm text-ink/75 leading-relaxed">
            Pay <strong className="font-outfit text-ink">$1.99</strong> to unlock the full-resolution file. Yours forever — they keep theirs too.
          </p>
          <button
            data-testid="gift-buy-btn"
            onClick={handleBuy}
            disabled={busy}
            className="neo-btn bg-lilac w-full h-12 text-base mt-5 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Awaiting payment…</>
            ) : (
              <><Lock className="h-4 w-4 mr-2" /> Buy HD · $1.99</>
            )}
          </button>

          {paymentStatus === 'awaiting' && (
            <div data-testid="gift-payment-status" className="border-2 border-ink bg-sun/40 p-3 rounded-sm mt-4">
              <p className="font-work text-xs leading-relaxed">
                A Stripe Checkout window has opened. Complete payment there — your HD file will download automatically.
              </p>
            </div>
          )}
          {paymentStatus === 'cancelled' && (
            <div data-testid="gift-payment-cancelled" className="border-2 border-ink bg-rose/40 p-3 rounded-sm mt-4">
              <p className="font-work text-xs">Payment cancelled. Try again whenever you'd like.</p>
            </div>
          )}

          <div className="mt-5 pt-4 border-t-2 border-ink/15 flex items-center gap-2 text-xs text-ink/60">
            <Sparkles className="h-3.5 w-3.5" />
            <span className="font-work">Stripe · Test mode</span>
          </div>
          <p className="font-work text-[10px] text-ink/40 mt-3">
            Created {new Date(meta.created_at).toLocaleDateString()} · {meta.view_count} view{meta.view_count === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      <a href="/" className="mt-12 font-work text-sm text-ink/60 underline underline-offset-4 hover:text-ink">
        Make your own collage →
      </a>
    </div>
  );
};

const CenteredCard = ({ children }) => (
  <div className="min-h-screen bg-canvas grain flex items-center justify-center p-6">
    <div className="neo-card p-10 text-center flex flex-col items-center max-w-md">
      {children}
    </div>
  </div>
);
